import { Button } from "@/components/ui/button";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { updateRoleData, getRolePermissions, getShowRole } from "@/Api/roles";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { useNavigate, useParams } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PageTemplate from "@/components/PageTemplate";
import { usePermissionCheck } from "@/components/PermissionGuard";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  permissions: z.array(z.number()).min(1, "Selecione pelo menos uma permissão"),
});

type SchemaType = z.infer<typeof schema>;

interface Permission {
  id: number;
  name: string;
  description: string;
  permission_group: {
    id: number;
    name: string;
  };
}

interface PermissionGroup {
  [key: string]: Permission[];
}

interface Role {
  id: number;
  name: string;
  permissions: Permission[];
}

export const UpdateRolePage = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { hasPermission } = usePermissionCheck();
  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      permissions: [],
    },
  });

  const { toast } = useToastNavigate();
  const navigate = useNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [permissions, setPermissions] = useState<PermissionGroup>({});
  const [loading, setLoading] = useState(true);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [role, setRole] = useState<Role | null>(null);

  // Verifica se o usuário tem permissão para editar roles
  useEffect(() => {
    if (!hasPermission("edit-role")) {
      navigate("/cargos");
      toast({
        description: "Você não tem permissão para acessar esta página",
        variant: "destructive",
      });
    }
  }, [hasPermission, navigate, toast]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [permissionsResponse, roleResponse] = await Promise.all([
          getRolePermissions(),
          getShowRole(uuid!),
        ]);

        if (permissionsResponse.ok && permissionsResponse.data?.response) {
          setPermissions(permissionsResponse.data.response);
        }

        if (roleResponse.ok && roleResponse.data?.response) {
          const roleData = roleResponse.data.response;
          setRole(roleData);
          form.setValue("name", roleData.name);

          const rolePermissionIds =
            roleData.permissions?.map((p: Permission) => p.id) || [];
          setSelectedPermissions(rolePermissionIds);
          form.setValue("permissions", rolePermissionIds);
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      } finally {
        setLoading(false);
      }
    };

    if (uuid) {
      fetchData();
    }
  }, [uuid, form]);

  const handlePermissionChange = (permissionId: number, checked: boolean) => {
    let newPermissions: number[];

    if (checked) {
      newPermissions = [...selectedPermissions, permissionId];
    } else {
      newPermissions = selectedPermissions.filter((id) => id !== permissionId);
    }

    setSelectedPermissions(newPermissions);
    form.setValue("permissions", newPermissions);
  };

  const handleGroupPermissionChange = (
    groupPermissions: Permission[],
    checked: boolean
  ) => {
    const groupPermissionIds = groupPermissions.map((p) => p.id);
    let newPermissions: number[];

    if (checked) {
      // Add all group permissions that aren't already selected
      const permissionsToAdd = groupPermissionIds.filter(
        (id) => !selectedPermissions.includes(id)
      );
      newPermissions = [...selectedPermissions, ...permissionsToAdd];
    } else {
      // Remove all group permissions
      newPermissions = selectedPermissions.filter(
        (id) => !groupPermissionIds.includes(id)
      );
    }

    setSelectedPermissions(newPermissions);
    form.setValue("permissions", newPermissions);
  };

  const isGroupFullySelected = (groupPermissions: Permission[]) => {
    const groupPermissionIds = groupPermissions.map((p) => p.id);
    return groupPermissionIds.every((id) => selectedPermissions.includes(id));
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const payload = {
      name: data.name,
      permissions: data.permissions,
    };

    const { data: apiResponse, ok } = await updateRoleData(uuid!, payload);
    if (!ok) {
      setFormState("error");
      return;
    }

    setFormState("default");
    toast({
      description: apiResponse?.message || "Cargo atualizado com sucesso",
      action: <Check color="#1edc2b" />,
    });

    navigate("/cargos");
  };

  if (loading) {
    return (
      <PageTemplate>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Editar Cargo</h1>
        </div>
        <div className="flex justify-center py-8">
          <LoaderSpin />
        </div>
      </PageTemplate>
    );
  }

  if (!role) {
    return (
      <PageTemplate>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Editar Cargo</h1>
        </div>
        <div className="flex justify-center py-8">
          <p>Cargo não encontrado</p>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Editar Cargo: {role.name}</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/cargos")}
          className="flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          Voltar
        </Button>
      </div>

      <div className="max-w-4xl">
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-6"
        >
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
            </CardHeader>
            <CardContent>
              <Form.Row>
                <Form.Field name="name">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Nome do Cargo <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input {...field} placeholder="Ex: Administrador" />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                Permissões
                <Form.Field name="permissions">
                  {() => (
                    <Form.Item>
                      <Form.Message className="text-sm" />
                    </Form.Item>
                  )}
                </Form.Field>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(permissions).map(
                ([groupName, groupPermissions]) => (
                  <div key={groupName} className="space-y-3">
                    <div className="flex items-center space-x-3 pb-2 border-b">
                      <Checkbox
                        id={`group-${groupName}`}
                        checked={isGroupFullySelected(groupPermissions)}
                        onCheckedChange={(checked) =>
                          handleGroupPermissionChange(
                            groupPermissions,
                            checked as boolean
                          )
                        }
                      />
                      <label
                        htmlFor={`group-${groupName}`}
                        className="text-base font-semibold leading-none cursor-pointer"
                      >
                        {groupName}
                      </label>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 ml-6">
                      {groupPermissions.map((permission) => (
                        <div
                          key={permission.id}
                          className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                        >
                          <Checkbox
                            id={`permission-${permission.id}`}
                            checked={selectedPermissions.includes(
                              permission.id
                            )}
                            onCheckedChange={(checked) =>
                              handlePermissionChange(
                                permission.id,
                                checked as boolean
                              )
                            }
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-1">
                            <label
                              htmlFor={`permission-${permission.id}`}
                              className="text-sm font-medium leading-none cursor-pointer"
                            >
                              {permission.description}
                            </label>
                            <p className="text-xs text-muted-foreground font-mono">
                              {permission.name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/cargos")}
              disabled={formState === "loading"}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className={cn(colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Atualizando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Atualizar Cargo"}
              {formState === "error" && "Erro ao atualizar"}
            </Button>
          </div>
        </Form>
      </div>
    </PageTemplate>
  );
};
